
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getJson, postJson } from '../services/http';
import useInstallPrompt, { trackInstallEvent } from '../hooks/useInstallPrompt';
import InstallCTA, { primaryLabelForMode } from './download/InstallCTA';
import InstallSheet from './download/InstallSheet';
import InstallIcon from './download/InstallIcon';
import './Download.css';

/**
 * Download Landing Page Component
 *
 * Color scheme:
 * - Primary Orange: #ff6800
 * - Blue3: #253866
 * - Gray: #3C3C3C
 */
function Download() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // PWA install: device-aware mode + native prompt (see useInstallPrompt).
  const { mode: installMode, platform: installPlatform, install } = useInstallPrompt();
  const [installSheetOpen, setInstallSheetOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Use-case tabs ("Running a fundraiser", …) — index of the selected tab.
  const [activeUseCase, setActiveUseCase] = useState(0);

  // Sticky mobile install bar: shown once the hero CTA scrolls out of view,
  // until dismissed for the session.
  const heroCtaRef = useRef(null);
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  const [stickyDismissed, setStickyDismissed] = useState(() => {
    try { return sessionStorage.getItem('dl-sticky-install-dismissed') === '1'; } catch { return false; }
  });

  // Subscription plans, shown in the public Pricing section
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Asset paths
  const HERO_IMAGE = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`;
  const SE3D_MARK = '/images/se3d-inc-mark-64.png';
  const SEO_IMAGE = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-fullres.png`;
  const DEMO_VIDEO = `${process.env.PUBLIC_URL}/videos/onscreen_animated_ad_v10.mp4`;
  // Real poster frame pulled from the demo video itself (a product-dashboard
  // beat ~9s in), so the player shows the product rather than the logo.
  const DEMO_POSTER = '/images/video-cover-demo.jpg';
  const VIDEOS = {
    createAccount: `${process.env.PUBLIC_URL}/videos/CreateAccount.mp4`,
    integrations: `${process.env.PUBLIC_URL}/videos/Integrations.mp4`,
    eventEventItem: `${process.env.PUBLIC_URL}/videos/Event-EventItem.mp4`,
    auctionBid: `${process.env.PUBLIC_URL}/videos/AuctionBid.mp4`,
    schedulePost: `${process.env.PUBLIC_URL}/videos/SchedulePost.mp4`,
    analytics: `${process.env.PUBLIC_URL}/videos/Analytics.mp4`,
  };
  // Cover frames for the "All Videos" CTA tile grid — one per video above.
  const VIDEO_COVERS = [
    '/images/video-cover-create-account.jpg',
    '/images/video-cover-integrations.jpg',
    '/images/video-cover-event-event-item.jpg',
    '/images/video-cover-auction-bid.jpg',
    '/images/video-cover-schedule-post.jpg',
    '/images/video-cover-analytics.jpg',
  ];
  // Generated by scripts/build-user-docs.js from the e2e/docs Playwright
  // suite. Filenames are produced from section.title in build-user-docs.js
  // — keep in sync if section titles change. `index` is the guide index
  // (all 18 sections) — every generic "Documentation" link on this page
  // points there; `s01`..`s18` are the individual section guides used by
  // the User Guides card grid below.
  const DOCS = {
    index: '/docs/index.html',
    s01: '/docs/OnScreen_01_Login.html',
    s02: '/docs/OnScreen_02_Create_Account_OwnerAdd.html',
    s03: '/docs/OnScreen_03_Owner_Login.html',
    s04: '/docs/OnScreen_04_Create_Tenant.html',
    s05: '/docs/OnScreen_05_Dashboard.html',
    s06: '/docs/OnScreen_06_Add_Tenant_User.html',
    s07: '/docs/OnScreen_07_Analytics.html',
    s08: '/docs/OnScreen_08_Social_Inbox.html',
    s09: '/docs/OnScreen_09_Post.html',
    s10: '/docs/OnScreen_10_Events.html',
    s11: '/docs/OnScreen_11_Membership.html',
    s12: '/docs/OnScreen_12_Tenant_Users.html',
    s13: '/docs/OnScreen_13_Social_Media_Integrations.html',
    s14: '/docs/OnScreen_14_Account_Settings.html',
    s15: '/docs/OnScreen_15_Billing.html',
    s16: '/docs/OnScreen_16_Navigation_Sidebar.html',
    s17: '/docs/OnScreen_17_Team_Collaboration.html',
    s18: '/docs/OnScreen_18_Member_Event_History.html',
  };
  // Absolute URL (not the relative DOCS.index) — used by the "All
  // Documentation" cards in the User Guides / Watch & Learn rows per
  // product request, so the link resolves correctly wherever it's shared.
  const ALL_DOCS_URL = 'https://on-screen.app/docs/index.html';

  useEffect(() => {
    document.body.classList.add('download-page-active');
    return () => document.body.classList.remove('download-page-active');
  }, []);

  // Fire-and-forget page-visit beacon for the super-admin "Site Visits" card.
  // Anonymous/public page — failures (offline, ad blockers) are silently ignored.
  useEffect(() => {
    postJson('/api/tracking/site-visit', { path: '/about' }).catch(() => {});
  }, []);

  // Public pricing plans (unauthenticated endpoint — same one the owner
  // sign-up flow uses to list plans before an account exists).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getJson('/api/billing/plans');
        if (!cancelled) setPlans(data.plans || []);
      } catch (err) {
        console.error('Failed to load subscription plans:', err);
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SEO/AEO: page-specific <head> tags + structured data for search engines
  // and AI crawlers. This is a client-rendered SPA with one static
  // index.html, so these only reach crawlers that execute JS (Googlebot,
  // Bingbot, and most current AI crawlers do; some text-only bots won't see
  // them) — public/index.html keeps the generic app-wide description as the
  // no-JS fallback for every other route.
  useEffect(() => {
    const seoTitle = t('download.seoTitle', 'On-Screen | One Platform for Events, Membership, and Social Media');
    const seoDescription = t(
      'download.seoDescription',
      'On-Screen is one platform for running events, managing membership, and staying connected on social media — pick what you need, or use it all. Install free on phone or desktop, no app store needed.'
    );
    const ogDescription = t(
      'download.seoOgDescription',
      'Pick what you need — event ticketing, membership check-in, social media scheduling, and more — all from one dashboard.'
    );
    const twitterDescription = t(
      'download.seoTwitterDescription',
      'One platform for events, membership, and social media. Pick what you need, or use it all.'
    );
    const pageUrl = 'https://on-screen.app/about';
    const imageUrl = `https://on-screen.app${SEO_IMAGE}`;
    const videoUrl = `https://on-screen.app${DEMO_VIDEO}`;
    const videoPosterUrl = `https://on-screen.app${DEMO_POSTER}`;

    const prevTitle = document.title;
    document.title = seoTitle;

    const createdEls = [];
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        createdEls.push(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('name', 'description', seoDescription);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', seoTitle);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:url', pageUrl);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:site_name', 'On-Screen');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seoTitle);
    upsertMeta('name', 'twitter:description', twitterDescription);
    upsertMeta('name', 'twitter:image', imageUrl);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      createdEls.push(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // SoftwareApplication + VideoObject structured data — helps search and
    // AI answer engines identify what On-Screen is without parsing prose.
    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'On-Screen',
          url: pageUrl,
          description: seoDescription,
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: { '@type': 'Organization', name: 'SE3D Inc.', url: 'https://on-screen.app' },
          image: imageUrl,
        },
        {
          '@type': 'VideoObject',
          name: 'On-Screen Product Demo',
          description: 'Plan and schedule posts, run events, and see what’s working — all in one place.',
          uploadDate: '2025-07-02',
          contentUrl: videoUrl,
          thumbnailUrl: videoPosterUrl,
        },
      ],
    });
    document.head.appendChild(structuredData);
    createdEls.push(structuredData);

    return () => {
      document.title = prevTitle;
      createdEls.forEach(el => el.remove());
    };
  }, [t, SEO_IMAGE, DEMO_VIDEO, DEMO_POSTER]);

  // Hide the sticky install bar while the hero's own install button is on screen.
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setHeroCtaVisible(entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleOwnerSignup = () => {
    navigate('/owneradd');
  };

  const handleOpenInBrowser = () => {
    trackInstallEvent('open-browser');
    navigate('/login');
  };

  // The one primary action, shared by the navbar, hero, final CTA and the
  // sticky bar: native prompt where we have one, otherwise the step sheet.
  const handleInstallAction = useCallback(async () => {
    if (installMode === 'installed' || installMode === 'unsupported') {
      navigate('/login');
      return;
    }
    if (installMode === 'prompt') {
      const outcome = await install();
      if (outcome !== 'unavailable') return;
    }
    trackInstallEvent('install-steps');
    setInstallSheetOpen(true);
  }, [installMode, install, navigate]);

  const closeInstallSheet = useCallback(() => setInstallSheetOpen(false), []);

  const dismissSticky = () => {
    setStickyDismissed(true);
    try { sessionStorage.setItem('dl-sticky-install-dismissed', '1'); } catch { /* ignore */ }
  };

  const scrollToId = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatPlanLimit = (value, unlimited) => (unlimited ? t('download.pricingUnlimited', 'Unlimited') : value);

  // Cheapest plan + longest trial for the hero's one-line pricing teaser.
  const cheapestPlan = plans.reduce(
    (min, p) => (min === null || Number(p.price_monthly) < Number(min.price_monthly) ? p : min),
    null
  );
  const maxTrialDays = plans.reduce((max, p) => Math.max(max, Number(p.trial_days) || 0), 0);

  // "No app store needed": why installing the web app is a feature.
  const installBenefits = [
    { icon: 'bolt', title: t('download.benefit.fastTitle', 'Installs in seconds'), desc: t('download.benefit.fastDesc', 'Straight from your browser. No app store, no download to wait on.') },
    { icon: 'refresh', title: t('download.benefit.updatedTitle', 'Always up to date'), desc: t('download.benefit.updatedDesc', 'New features arrive automatically. Nothing to update.') },
    { icon: 'devices', title: t('download.benefit.devicesTitle', 'Phone, tablet, and desktop'), desc: t('download.benefit.devicesDesc', 'One account on every device: scan at the door, plan at your desk.') },
    { icon: 'bell', title: t('download.benefit.pushTitle', 'Push notifications'), desc: t('download.benefit.pushDesc', 'Know the moment a ticket sells, a bid lands, or a message arrives.') },
    { icon: 'feather', title: t('download.benefit.lightTitle', 'Light on storage'), desc: t('download.benefit.lightDesc', 'A fraction of the space a store app takes. Uninstall any time.') },
  ];

  // Module grid: replaces the audience list as the thing that explains breadth.
  const modules = [
    { icon: 'ticket', title: t('download.module.ticketingTitle', 'Ticketing'), desc: t('download.module.ticketingDesc', 'Sell tickets, registrations, and free sign-ups with tiers and capacity.') },
    { icon: 'qr', title: t('download.module.checkinTitle', 'Check-in'), desc: t('download.module.checkinDesc', 'Scan QR tickets at the door, add walk-ins, and watch the roster fill.') },
    { icon: 'calendar', title: t('download.module.socialTitle', 'Social posting'), desc: t('download.module.socialDesc', 'Write once, schedule everywhere, and repeat weekly posts automatically.') },
    { icon: 'gift', title: t('download.module.rafflesTitle', 'Raffles, auctions & contests'), desc: t('download.module.rafflesDesc', 'Run online entries and bidding with winners drawn for you.') },
    { icon: 'chat', title: t('download.module.messagingTitle', 'Messaging & inbox'), desc: t('download.module.messagingDesc', 'Comments, mentions, and messages from every network in one inbox.') },
    { icon: 'tasks', title: t('download.module.tasksTitle', 'Tasks & team'), desc: t('download.module.tasksDesc', 'Assign the to-dos behind every event and see who’s on what.') },
    { icon: 'image', title: t('download.module.mediaTitle', 'Media editing'), desc: t('download.module.mediaDesc', 'Crop, adjust, and add music to photos and video before you post.') },
    { icon: 'users', title: t('download.module.membershipTitle', 'Membership'), desc: t('download.module.membershipDesc', 'A member directory with roles, check-ins, and event history.') },
  ];

  // Use-case tabs: lets a sneaker shop and a church each find themselves
  // without naming either in the headline. Screens are real seeded data.
  const useCases = [
    {
      key: 'fundraiser',
      label: t('download.useCase.fundraiserLabel', 'Running a fundraiser'),
      title: t('download.useCase.fundraiserTitle', 'Sell raffle entries online and post about it the same afternoon'),
      desc: t('download.useCase.fundraiserDesc', 'Set up the raffle, share the link to every network, and draw the winner at the event. Tasks and vendors stay in the same place.'),
      modules: [t('download.module.rafflesTitle', 'Raffles, auctions & contests'), t('download.module.socialTitle', 'Social posting'), t('download.module.tasksTitle', 'Tasks & team')],
      image: '/images/install/fundraiser.webp',
      alt: t('download.useCase.fundraiserAlt', 'Public raffle entry page for a charity fundraiser'),
    },
    {
      key: 'drop',
      label: t('download.useCase.dropLabel', 'Launching a product drop'),
      title: t('download.useCase.dropTitle', 'Run a fair release raffle instead of a first-come stampede'),
      desc: t('download.useCase.dropDesc', 'Snap the product, post the drop to Instagram, TikTok, and X, take raffle entries per size, and verify winners at pickup.'),
      modules: [t('download.module.mediaTitle', 'Media editing'), t('download.module.socialTitle', 'Social posting'), t('download.module.rafflesTitle', 'Raffles, auctions & contests')],
      image: '/images/install/drop.webp',
      alt: t('download.useCase.dropAlt', 'Sneaker release raffle entry page'),
    },
    {
      key: 'show',
      label: t('download.useCase.showLabel', 'Hosting a show'),
      title: t('download.useCase.showTitle', 'Tickets at the door, a VIP auction online, and the lineup posted every week'),
      desc: t('download.useCase.showDesc', 'Sell tiers, auction a backstage experience, schedule a recurring lineup post, and scan tickets at the door from your phone.'),
      modules: [t('download.module.ticketingTitle', 'Ticketing'), t('download.module.checkinTitle', 'Check-in'), t('download.module.rafflesTitle', 'Raffles, auctions & contests')],
      image: '/images/install/show.webp',
      alt: t('download.useCase.showAlt', 'Live auction bidding page showing the current highest bid'),
    },
    {
      key: 'members',
      label: t('download.useCase.membersLabel', 'Managing members & volunteers'),
      title: t('download.useCase.membersTitle', 'Fill volunteer shifts and know who showed up'),
      desc: t('download.useCase.membersDesc', 'Publish shifts as free sign-ups, refill cancellations from the waitlist, send reminders, and check people in on the day.'),
      modules: [t('download.module.ticketingTitle', 'Ticketing'), t('download.module.membershipTitle', 'Membership'), t('download.module.checkinTitle', 'Check-in')],
      image: '/images/install/volunteers.webp',
      alt: t('download.useCase.membersAlt', 'Volunteer shift sign-up page with time slots'),
    },
  ];

  const onUseCaseKeyDown = (e) => {
    const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (delta === undefined) return;
    e.preventDefault();
    const next = (activeUseCase + delta + useCases.length) % useCases.length;
    setActiveUseCase(next);
    document.getElementById(`usecase-tab-${next}`)?.focus();
  };

  // Trust row — every claim here is backed by the product or the legal docs.
  const trustItems = [
    { icon: 'card', title: t('download.trust.stripeTitle', 'Payments by Stripe'), desc: t('download.trust.stripeDesc', 'Tickets, bids, and entries are processed by Stripe.') },
    { icon: 'shield', title: t('download.trust.privacyTitle', 'We don’t sell your data'), desc: t('download.trust.privacyDesc', 'Read exactly what we collect in our privacy policy.'), href: '/privacypolicy' },
    { icon: 'lock', title: t('download.trust.secureTitle', 'Secure by default'), desc: t('download.trust.secureDesc', 'HTTPS everywhere, with role-based access for your team.') },
    { icon: 'globe', title: t('download.trust.webTitle', 'Install from the web'), desc: t('download.trust.webDesc', 'No app-store account or approval needed.') },
  ];

  // PWA-specific questions that otherwise stop people from installing.
  const faqs = [
    { q: t('download.faq.whatQ', 'What does installing a web app mean?'), a: t('download.faq.whatA', 'On-Screen is a progressive web app (PWA). Installing adds it to your home screen, dock, or taskbar so it opens full-screen in its own window, just like an app from a store, without going through one.') },
    { q: t('download.faq.costQ', 'Does installing cost anything?'), a: t('download.faq.costA', 'No. Installing is free and you don’t need an account to do it. You choose a plan when you create your organization’s account.') },
    { q: t('download.faq.offlineQ', 'Does it work offline?'), a: t('download.faq.offlineA', 'The app itself is saved to your device so it opens quickly, even on a weak connection. Live data like ticket sales, check-ins, and scheduled posts needs an internet connection to stay in sync.') },
    { q: t('download.faq.notifyQ', 'Will I get notifications?'), a: t('download.faq.notifyA', 'Yes. You can turn on push notifications in Settings. On iPhone and iPad, notifications only work after On-Screen is added to your home screen (iOS 16.4 or later).') },
    { q: t('download.faq.devicesQ', 'Can I use it on more than one device?'), a: t('download.faq.devicesA', 'Yes. Sign in on your phone, tablet, and computer with the same account. Everything stays in sync.') },
    { q: t('download.faq.updateQ', 'How do updates work?'), a: t('download.faq.updateA', 'Automatically. The next time you open On-Screen you’re on the latest version. There’s nothing to download.') },
    { q: t('download.faq.uninstallQ', 'How do I uninstall it?'), a: t('download.faq.uninstallA', 'Remove it like any other app: long-press the icon on your phone, or right-click it on your computer and choose Uninstall. Your account and data stay safe in the cloud.') },
  ];

  // Quick start videos with icons
  const videoGuides = [
    {
      title: t('download.videoCreateAccountTitle', 'Create an Account'),
      description: t('download.videoCreateAccountDesc', 'Sign up and get your account set up in minutes.'),
      src: VIDEOS.createAccount,
    },
    {
      title: t('download.videoIntegrationsTitle', 'Integrations'),
      description: t('download.videoIntegrationsDesc', 'Connect your social media platforms to On-Screen.'),
      src: VIDEOS.integrations,
    },
    {
      title: t('download.videoEventEventItemTitle', 'Events & Event Items'),
      description: t('download.videoEventEventItemDesc', 'Create events and add event items like auctions and raffles.'),
      src: VIDEOS.eventEventItem,
    },
    {
      title: t('download.videoAuctionBidTitle', 'Auction Bidding'),
      description: t('download.videoAuctionBidDesc', 'See how members place bids on auction items.'),
      src: VIDEOS.auctionBid,
    },
    {
      title: t('download.videoSchedulePostTitle', 'Schedule a Post'),
      description: t('download.videoSchedulePostDesc', 'Schedule posts across your connected platforms.'),
      src: VIDEOS.schedulePost,
    },
    {
      title: t('download.videoAnalyticsTitle', 'Analytics'),
      description: t('download.videoAnalyticsDesc', 'Track engagement, reach, and post performance.'),
      src: VIDEOS.analytics,
    },
  ];

  // Full set of 18 documentation guides. Only the first 3 render as cards
  // in the User Guides section (the 4th slot is the "All Documentation"
  // link card) — kept as one list so the section stays a single source of
  // truth for guide copy/links.
  const userGuides = [
    { title: t('download.guide1Title'),  file: DOCS.s01, desc: t('download.guide1Desc') },
    { title: t('download.guide2Title'),  file: DOCS.s02, desc: t('download.guide2Desc') },
    { title: t('download.guide3Title'),  file: DOCS.s03, desc: t('download.guide3Desc') },
    { title: t('download.guide4Title'),  file: DOCS.s04, desc: t('download.guide4Desc') },
    { title: t('download.guide5Title'),  file: DOCS.s05, desc: t('download.guide5Desc') },
    { title: t('download.guide6Title'),  file: DOCS.s06, desc: t('download.guide6Desc') },
    { title: t('download.guide7Title'),  file: DOCS.s07, desc: t('download.guide7Desc') },
    { title: t('download.guide8Title'),  file: DOCS.s08, desc: t('download.guide8Desc') },
    { title: t('download.guide9Title'),  file: DOCS.s09, desc: t('download.guide9Desc') },
    { title: t('download.guide10Title'), file: DOCS.s10, desc: t('download.guide10Desc') },
    { title: t('download.guide11Title'), file: DOCS.s11, desc: t('download.guide11Desc') },
    { title: t('download.guide12Title'), file: DOCS.s12, desc: t('download.guide12Desc') },
    { title: t('download.guide13Title'), file: DOCS.s13, desc: t('download.guide13Desc') },
    { title: t('download.guide14Title'), file: DOCS.s14, desc: t('download.guide14Desc') },
    { title: t('download.guide15Title'), file: DOCS.s15, desc: t('download.guide15Desc') },
    { title: t('download.guide16Title'), file: DOCS.s16, desc: t('download.guide16Desc') },
    { title: t('download.guide17Title'), file: DOCS.s17, desc: t('download.guide17Desc') },
    { title: t('download.guide18Title'), file: DOCS.s18, desc: t('download.guide18Desc') },
  ];

  return (
    <div className="download-page modern">
      {/* Navigation Bar */}
      <nav className="dl-navbar">
        <div className="dl-navbar-inner">
          <img
            src={HERO_IMAGE}
            alt="On-Screen"
            className="dl-navbar-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
          <div className={`dl-navbar-links${mobileMenuOpen ? ' open' : ''}`}>
            <button className="dl-navbar-link" onClick={() => scrollToId('features')}>
              {t('download.navFeatures', 'Features')}
            </button>
            <button className="dl-navbar-link" onClick={() => scrollToId('pricing-section')}>
              {t('download.navPricing', 'Pricing')}
            </button>
            <button className="dl-navbar-link" onClick={() => { setMobileMenuOpen(false); window.open(DOCS.index, '_blank'); }}>
              {t('download.navDocumentation', 'Documentation')}
            </button>
            <button className="dl-navbar-link" onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}>
              {t('download.navSignIn', 'Sign In')}
            </button>
            <button className="dl-navbar-cta" onClick={() => { setMobileMenuOpen(false); handleInstallAction(); }}>
              {installMode === 'installed'
                ? t('download.cta.open', 'Open On-Screen')
                : t('download.navGetApp', 'Get the app')}
            </button>
          </div>
          <button className="dl-navbar-toggle" onClick={() => setMobileMenuOpen(v => !v)} aria-label="Toggle menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-modern">
        <div className="hero-gradient"></div>
        <div className="hero-pattern"></div>

        <div className="dl-container">
          <div className="hero-content-modern">
            <div className="hero-text-modern">
              <p className="hero-eyebrow">
                <InstallIcon name="phone" />
                {t('download.heroEyebrow', 'Free to install · No app store needed')}
              </p>
              <h1 className="hero-title-modern">
                {t('download.heroHeadlineLead', 'Everything you run, on ')}
                <span className="gradient-text">{t('download.heroHeadlineAccent', 'one screen.')}</span>
              </h1>
              <p className="hero-description-modern">
                {t('download.heroSubhead', 'Ticketing, check-in, social posting, raffles, and messaging in one app instead of five. Use the tools you need and add the rest when you’re ready.')}
              </p>

              <div ref={heroCtaRef} id="install-section">
                <InstallCTA
                  mode={installMode}
                  onPrimary={handleInstallAction}
                  onOpenBrowser={handleOpenInBrowser}
                  variant="hero"
                  showOtherDevices
                />
              </div>

              {cheapestPlan && (
                <button type="button" className="hero-pricing-teaser" onClick={() => scrollToId('pricing-section')}>
                  {maxTrialDays > 0
                    ? t('download.heroPricingTrial', 'Plans from ${{price}}/mo · {{days}}-day free trial', {
                      price: Number(cheapestPlan.price_monthly).toFixed(0),
                      days: maxTrialDays,
                    })
                    : t('download.heroPricing', 'Plans from ${{price}}/mo', {
                      price: Number(cheapestPlan.price_monthly).toFixed(0),
                    })}
                  <InstallIcon name="arrowRight" />
                </button>
              )}
            </div>

            <div className="hero-visual hero-devices" aria-hidden="true">
              <div className="device-phone device-phone-back">
                <img src="/images/install/checkin.webp" alt="" loading="eager" width="480" height="1039" />
              </div>
              <div className="device-phone device-phone-front">
                <img src="/images/install/scheduled.webp" alt="" loading="eager" width="480" height="1039" />
              </div>
              <div className="device-chip device-chip-checkin">
                <InstallIcon name="qr" />
                {t('download.heroChipCheckin', 'Checked in at the door')}
              </div>
              <div className="device-chip device-chip-posts">
                <InstallIcon name="calendar" />
                {t('download.heroChipPosts', 'Posts queued for the week')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Integrations Strip */}
      <section className="platforms-strip">
        <div className="dl-container">
          <div className="platforms-label">{t('download.trustedPlatforms', 'Works With Your Social Media Accounts')}</div>
          <div className="platforms-row">
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              <span>Instagram</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span>Facebook</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.781 3.631 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.757-.513-.586-1.302-.883-2.346-.89h-.030c-.84 0-1.98.232-2.708 1.317L7.74 8.785c.97-1.45 2.547-2.247 4.5-2.247h.043c3.260.02 5.196 2.026 5.39 5.527.111.046.22.094.328.144 1.527.717 2.644 1.805 3.231 3.146.819 1.872.895 4.926-1.602 7.367-1.908 1.867-4.225 2.71-7.443 2.731Zm1.062-11.114c-.234 0-.471.007-.713.022-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.84 2.784 1.768 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.24-.222Z"/></svg>
              <span>Threads</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              <span>X</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              <span>LinkedIn</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              <span>TikTok</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              <span>YouTube</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.574 6.27.584 1.834 2.287 2.489 3.87 2.295 0 0-4.228 5.525 5.182 9.667C14.414 24.812 17.456 24 18.35 24c.575 0 4.028-2.564 4.028-2.564.767-.522.59-1.26.34-1.7-.676-1.195-2.846-3.597-4.04-4.357-1.087-.69-2.137-.396-2.855.195 0 0-.576.472-1.017.838-.257.213-.617.166-.834-.02-1.372-1.178-2.477-2.86-2.477-2.86s1.682-1.105 2.86-2.477c.186-.217.233-.577.02-.834-.366-.441-.838-1.017-.838-1.017-.591-.718-.885-1.768-.195-2.855.76-1.194 3.162-3.364 4.357-4.04.44-.25 1.178-.427 1.7.34 0 0 2.564 3.453 2.564 4.028 0 .894.812 3.936-1.999 8.724"/></svg>
              <span>Bluesky</span>
            </div>
            {/* <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.884-7.003 4.884-3.874 0-7.004-2.19-7.004-4.884 0-.18.015-.36.043-.52A1.755 1.755 0 0 1 4.028 12a1.755 1.755 0 0 1 2.96-.97c1.19-.855 2.845-1.416 4.66-1.49l.899-4.208a.339.339 0 0 1 .402-.272l2.913.613a1.25 1.25 0 0 1 1.148-.769zM9.3 12.645a1.25 1.25 0 0 0-1.248 1.25 1.25 1.25 0 0 0 1.249 1.249 1.25 1.25 0 0 0 1.249-1.25 1.25 1.25 0 0 0-1.25-1.248zm5.4 0a1.25 1.25 0 0 0-1.25 1.25 1.25 1.25 0 0 0 1.25 1.249 1.25 1.25 0 0 0 1.249-1.25 1.25 1.25 0 0 0-1.25-1.248zm-5.467 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
              <span>Reddit</span>
            </div>
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
              <span>Twitch</span>
            </div> */}
            <div className="platform-item">
              <svg viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24 18.635 24 24.001 18.633 24.001 12.017 24.001 5.396 18.635 0 12.017 0z"/></svg>
              <span>Pinterest</span>
            </div>
          </div>
        </div>
      </section>

      {/* No app store needed */}
      <section className="benefits-strip" aria-labelledby="benefits-title">
        <div className="dl-container">
          <h2 id="benefits-title" className="benefits-title">
            {t('download.benefitsTitle', 'No app store. No waiting. Just tap install.')}
          </h2>
          <ul className="benefits-grid">
            {installBenefits.map((b) => (
              <li key={b.title} className="benefit-item">
                <span className="benefit-icon"><InstallIcon name={b.icon} /></span>
                <span className="benefit-title">{b.title}</span>
                <span className="benefit-desc">{b.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Module grid */}
      <section className="features-modern" id="features">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.modulesTitle', 'Use one or use them all')}</h2>
            <p className="section-description-modern">
              {t('download.modulesDescription', 'Every plan includes every tool. Turn on what you need today and the rest is there when you’re ready.')}
            </p>
          </div>
          <ul className="module-grid">
            {modules.map((m) => (
              <li key={m.title} className="module-tile">
                <span className="module-icon"><InstallIcon name={m.icon} /></span>
                <h3 className="module-title">{m.title}</h3>
                <p className="module-desc">{m.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Use-case tabs */}
      <section className="usecases" aria-labelledby="usecases-title">
        <div className="dl-container">
          <div className="section-header">
            <h2 id="usecases-title" className="section-title-modern">{t('download.useCasesTitle', 'See it in action')}</h2>
            <p className="section-description-modern">
              {t('download.useCasesDescription', 'Real screens from real setups. Pick the one that looks like your week.')}
            </p>
          </div>
          <div className="usecase-tabs" role="tablist" aria-label={t('download.useCasesTitle', 'See it in action')}>
            {useCases.map((uc, i) => (
              <button
                key={uc.key}
                id={`usecase-tab-${i}`}
                type="button"
                role="tab"
                aria-selected={activeUseCase === i}
                aria-controls={`usecase-panel-${i}`}
                tabIndex={activeUseCase === i ? 0 : -1}
                className={`usecase-tab${activeUseCase === i ? ' active' : ''}`}
                onClick={() => setActiveUseCase(i)}
                onKeyDown={onUseCaseKeyDown}
              >
                {uc.label}
              </button>
            ))}
          </div>
          {useCases.map((uc, i) => (
            <div
              key={uc.key}
              id={`usecase-panel-${i}`}
              role="tabpanel"
              aria-labelledby={`usecase-tab-${i}`}
              className="usecase-panel"
              hidden={activeUseCase !== i}
            >
              <div className="usecase-copy">
                <h3 className="usecase-title">{uc.title}</h3>
                <p className="usecase-desc">{uc.desc}</p>
                <ul className="usecase-modules">
                  {uc.modules.map((m) => <li key={m}>{m}</li>)}
                </ul>
                <button type="button" className="cta-button primary usecase-cta" onClick={handleInstallAction}>
                  <InstallIcon name={installMode === 'installed' ? 'arrowRight' : 'download'} />
                  {primaryLabelForMode(installMode, t)}
                </button>
              </div>
              <div className="usecase-device">
                <div className="device-phone">
                  <img src={uc.image} alt={uc.alt} loading="lazy" width="480" height="1039" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust row */}
      <section className="trust-row" aria-label={t('download.trustLabel', 'Security and privacy')}>
        <div className="dl-container">
          <ul className="trust-grid">
            {trustItems.map((item) => (
              <li key={item.title} className="trust-item">
                <span className="trust-icon"><InstallIcon name={item.icon} /></span>
                <span>
                  <span className="trust-title">{item.title}</span>
                  {item.href
                    ? <a className="trust-desc" href={item.href}>{item.desc}</a>
                    : <span className="trust-desc">{item.desc}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* About On-Screen Section */}
      <section className="about-writeup" id="about-on-screen">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.aboutTitle', 'About On-Screen')}</h2>
          </div>
          <div className="about-writeup-body">
            <p>{t('download.aboutParagraph1', 'Running an organization or event often means juggling five different tools — one for membership, another for ticketing, another for social media, another for team communication, and something else entirely for sharing photos, updates, and announcements. Each may do its own job well, but they rarely work together, leaving organizers to connect the pieces themselves.')}</p>
            <p>{t('download.aboutParagraph2', "On-Screen closes that gap. Built by SE3D Inc., it's a modular platform that brings member management, event management, social media, and communication into one place. Start with what you need today, and add more as your organization grows — no stitching required.")}</p>
          </div>
          <blockquote className="about-pullquote">
            {t('download.aboutParagraph3', 'One platform. Every piece connected.')}
          </blockquote>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-modern" id="pricing-section">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.pricingTitle', 'Subscription Plans')}</h2>
            <p className="section-description-modern">
              {t('download.pricingDescription', 'Simple, transparent pricing that scales with your team.')}
            </p>
          </div>

          {plansLoading ? (
            <p className="pricing-loading">{t('download.pricingLoading', 'Loading plans…')}</p>
          ) : plans.length === 0 ? (
            <p className="pricing-loading">{t('download.pricingUnavailable', 'Pricing is temporarily unavailable. Please check back soon.')}</p>
          ) : (
            <div className="pricing-grid">
              {plans.map((plan) => (
                <div key={plan.id} className="pricing-card">
                  <h3 className="pricing-card-name">{plan.name}</h3>
                  <div className="pricing-card-price">
                    <span className="pricing-card-amount">${Number(plan.price_monthly).toFixed(2)}</span>
                    <span className="pricing-card-period">/{t('download.pricingPerMonth', 'mo')}</span>
                  </div>
                  {plan.trial_days > 0 && (
                    <div className="pricing-card-trial">
                      {t('download.pricingTrialDays', { defaultValue: '{{days}}-day free trial', days: plan.trial_days })}
                    </div>
                  )}
                  <ul className="pricing-card-features">
                    <li>{formatPlanLimit(plan.included_users, plan.is_unlimited_users)} {t('download.pricingUsers', 'users')}</li>
                    <li>{t('download.pricingUnlimited', 'Unlimited')} {t('download.pricingIntegrations', 'integrations')}</li>
                    <li>{formatPlanLimit(plan.included_posts_monthly, plan.is_unlimited_posts)} {t('download.pricingScheduledPosts', 'scheduled posts/mo')}</li>
                  </ul>
                  <button className="cta-button primary pricing-card-cta" onClick={handleOwnerSignup}>
                    {t('download.pricingGetStarted', 'Get Started')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Install FAQ */}
      <section className="faq-modern" id="faq" aria-labelledby="faq-title">
        <div className="dl-container">
          <div className="section-header">
            <h2 id="faq-title" className="section-title-modern">{t('download.faqTitle', 'Questions about installing')}</h2>
          </div>
          <div className="faq-list">
            {faqs.map((f) => (
              <details key={f.q} className="faq-item">
                <summary>
                  <span>{f.q}</span>
                  <InstallIcon name="chevronDown" className="faq-chevron" />
                </summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Product Demo Section */}
      <section className="demo-modern">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.demoTitle', 'What You Can Do With On-Screen')}</h2>
            <p className="section-description-modern">
              {t('download.demoDescription', 'Plan and schedule posts, run events, and see what’s working — all in one place.')}
            </p>
          </div>
          <div className="demo-video-wrap">
            <video
              className="demo-video"
              controls
              preload="metadata"
              poster={DEMO_POSTER}
              aria-label={t('download.demoTitle', 'What You Can Do With On-Screen')}
            >
              <source src={DEMO_VIDEO} type="video/mp4" />
              {t('download.demoVideoUnsupported', "Your browser doesn't support embedded video. You can download the demo instead.")}
            </video>
          </div>
        </div>
      </section>

      {/* User Guides Section */}
      <section className="quick-links-modern quick-links-guides">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.userGuidesTitle')}</h2>
            <p className="section-description-modern">
              {t('download.stepByStepGuidesMessage')}
            </p>
          </div>

          <div className="quick-links-grid guides-row-grid">
            {userGuides.slice(0, 3).map((guide, i) => (
              <button
                key={i}
                className="quick-link-card"
                onClick={() => window.open(guide.file, '_blank')}
              >
                <div className="quick-link-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586
                        a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="quick-link-title">{guide.title}</h3>
                <p className="quick-link-description">{guide.desc}</p>
                <div className="quick-link-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
            <a
              href={ALL_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-link-card quick-link-card-cta"
            >
              <div className="quick-link-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13
                      C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477
                      14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477
                      18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="quick-link-title">{t('download.allDocumentationButton', 'All Documentation')}</h3>
              <p className="quick-link-description">{t('download.allDocumentationGuidesDesc', 'Browse every step-by-step guide in one place.')}</p>
              <div className="quick-link-arrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Video Guides Section */}
      <section className="video-guides-modern">
        <div className="dl-container">
          <div className="section-header">
            <h2 className="section-title-modern">{t('download.videoGuidesTitle', 'Watch & Learn')}</h2>
            <p className="section-description-modern">
              {t('download.videoGuidesDescription', 'Short walkthroughs of the core On-Screen workflows.')}
            </p>
          </div>

          <div className="video-guides-grid">
            {videoGuides.slice(0, 2).map((guide, index) => (
              <div key={index} className="video-guide-card">
                <video
                  className="video-guide-video"
                  controls
                  preload="metadata"
                  aria-label={guide.title}
                >
                  <source src={guide.src} type="video/mp4" />
                  {t('download.demoVideoUnsupported', "Your browser doesn't support embedded video. You can download the demo instead.")}
                </video>
                <div className="video-guide-body">
                  <h3 className="video-guide-title">{guide.title}</h3>
                  <p className="video-guide-description">{guide.description}</p>
                </div>
              </div>
            ))}
            <a
              href={ALL_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="video-guide-card video-guide-card-cta"
            >
              <div className="video-guide-cta-media">
                <div className="video-guide-cta-grid">
                  {VIDEO_COVERS.map((src, index) => (
                    <img key={index} src={src} alt="" className="video-guide-cta-thumb" />
                  ))}
                </div>
                <div className="video-guide-cta-overlay">
                  <svg className="video-guide-cta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              <div className="video-guide-body">
                <h3 className="video-guide-title">{t('download.allVideosButton', 'All Videos')}</h3>
                <p className="video-guide-description">{t('download.watchMoreVideosDesc', 'Watch every walkthrough video on the documentation site.')}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-modern">
        <div className="cta-gradient"></div>
        <div className="dl-container">
          <div className="cta-content-modern">
            <h2 className="cta-title-modern">{t('download.finalCtaTitle', 'Put it on your home screen.')}</h2>
            <p className="cta-description-modern">
              {t('download.finalCtaDescription', 'Install On-Screen free, then create your organization’s account when you’re ready.')}
            </p>
            <InstallCTA
              mode={installMode}
              onPrimary={handleInstallAction}
              onOpenBrowser={handleOpenInBrowser}
              variant="final"
            />
            <div className="cta-buttons-modern">
              <button className="cta-button secondary large" onClick={handleOwnerSignup}>
                {t('download.signUpFreeButton')}
              </button>
              <button className="cta-button secondary large" onClick={handleGetStarted}>
                {t('download.signInButton')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-modern">
        <div className="dl-container">
          <div className="footer-content-modern">
            <div className="footer-column">
              <h4>{t('download.footerAboutHeading')}</h4>
              <a href="/about-me">{t('download.footerAboutMeLink')}</a>
            </div>

            <div className="footer-column">
              <h4>{t('download.footerProductHeading')}</h4>
              <a href="/login">{t('download.footerSignInLink')}</a>
              <a href="/owneradd">{t('download.footerSignUpLink')}</a>
              {/* <a href="https://scribehow.com/viewer/Create_Account_Login_Post_Content_and_Manage_Tasks__peI0qKp-TrSDsQXYQFeW1g" target="_blank" rel="noopener noreferrer">Documentation</a> */}
              <a href={DOCS.index} target="_blank" rel="noopener noreferrer">{t('download.footerDocumentationLink')}</a>
              <a href="#pricing-section">{t('download.footerPricingLink', 'Subscription Plans')}</a>
            </div>

            <div className="footer-column">
              <h4>{t('download.footerLegalHeading')}</h4>
              <a href="/termsofuse">{t('download.footerTermsLink')}</a>
              <a href="/privacypolicy">{t('download.footerPrivacyLink')}</a>
            </div>

            <div className="footer-column">
              <h4>{t('download.footerSupportHeading')}</h4>
              <a href="/sendemail">{t('download.footerContactLink')}</a>
            </div>
          </div>

          <div className="footer-company">
            <img src={SE3D_MARK} alt={t('download.companyName')} className="company-logo" />
            <div className="company-text">
              <div className="company-name">{t('download.companyName')}</div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>{t('common.copyrightText', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>

      {installMode !== 'installed' && !heroCtaVisible && !stickyDismissed && (
        <div className="sticky-install" role="region" aria-label={t('download.stickyLabel', 'Install On-Screen')}>
          <img src="/apple-touch-icon.png" alt="" className="sticky-install-icon" />
          <div className="sticky-install-text">
            <strong>On-Screen</strong>
            <span>{t('download.stickySub', 'Free · no app store')}</span>
          </div>
          <button type="button" className="cta-button primary sticky-install-btn" onClick={handleInstallAction}>
            {installMode === 'unsupported' ? t('download.cta.openShort', 'Open') : t('download.cta.installShort', 'Install')}
          </button>
          <button type="button" className="sticky-install-close" onClick={dismissSticky} aria-label={t('common.close', 'Close')}>
            <InstallIcon name="close" />
          </button>
        </div>
      )}

      <InstallSheet
        open={installSheetOpen}
        mode={installMode}
        platform={installPlatform}
        onClose={closeInstallSheet}
      />
    </div>
  );
}

export default Download;
